const height = {
    name: "height",
    path: "/height",
    title: "height",
    description: "Utilities for controlling the height of elements.",
    value: {
        "height": {
            "h-<number>": "calc(var(--1) * <number>)",
            "h-<fraction>": "calc(<fraction> * 100%)",
            "h-3xs": "var(--container-3xs); /* 16rem (256px) */",
            "h-2xs": "var(--container-2xs); /* 18rem (288px) */",
            "h-xs": "var(--container-xs); /* 20rem (320px) */",
            "h-sm": "var(--container-sm); /* 24rem (384px) */",
            "h-md": "var(--container-md); /* 28rem (448px) */",
            "h-lg": "var(--container-lg); /* 32rem (512px) */",
            "h-xl": "var(--container-xl); /* 36rem (576px) */",
            "h-2xl": "var(--container-2xl); /* 42rem (672px) */",
            "h-3xl": "var(--container-3xl); /* 48rem (768px) */",
            "h-4xl": "var(--container-4xl); /* 56rem (896px) */",
            "h-5xl": "var(--container-5xl); /* 64rem (1024px) */",
            "h-6xl": "var(--container-6xl); /* 72rem (1152px) */",
            "h-7xl": "var(--container-7xl); /* 80rem (1280px) */",
            "h-auto": "auto",
            "h-px": "1px",
            "h-full": "100%",
            "h-screen": "100vh",
            "h-dvh": "100dvh",
            "h-dvw": "100dvw",
            "h-lvh": "100lvh",
            "h-lvw": "100lvw",
            "h-svh": "100svh",
            "h-svw": "100svw",
            "h-min": "min-content",
            "h-max": "max-content",
            "h-fit": "fit-content",
            "h-[<value>]": "<value>",
        },
        "":{
            "size-auto": "width: auto; height: auto",
            "size-px": "width: 1px; height: 1px",
            "size-full": "width: 100%; height: 100%",
            "size-dvw": "width: 100dvw; height: 100dvw",
            "size-dvh": "width: 100dvh; height: 100dvh",
            "size-lvw": "width: 100lvw; height: 100lvw",
            "size-lvh": "width: 100lvh; height: 100lvh",
            "size-svw": "width: 100svw; height: 100svw",
            "size-svh": "width: 100svh; height: 100svh",
            "size-min": "width: min-content; height: min-content",
            "size-max": "width: max-content; height: max-content",
            "size-fit": "width: fit-content; height: fit-content",
            "size-[<value>]": "width: <value>; height: <value>",
            "size-<fraction>": "width: calc(<fraction> * 100%); height: calc(<fraction> * 100%)",
        }
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="h-96 bg-color-blue-500 rounded-lg p-2 text-center">h-96</div>
<div class="h-80 bg-color-blue-500 rounded-lg p-2 text-center">h-80</div>
<div class="h-64 bg-color-blue-500 rounded-lg p-2 text-center">h-64</div>
<div class="h-48 bg-color-blue-500 rounded-lg p-2 text-center">h-48</div>
<div class="h-40 bg-color-blue-500 rounded-lg p-2 text-center">h-40</div>
<div class="h-32 bg-color-blue-500 rounded-lg p-2 text-center">h-32</div>
<div class="h-24 bg-color-blue-500 rounded-lg p-2 text-center">h-24</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="flex flex-col h-64 ...">
  <div class="h-1/2 ...">h-1/2</div>
  <div class="h-1/2 ...">h-1/2</div>
</div>
<div class="flex flex-col h-64 ...">
  <div class="h-2/5 ...">h-2/5</div>
  <div class="h-3/5 ...">h-3/5</div>
</div>
<div class="flex flex-col h-64 ...">
  <div class="h-1/3 ...">h-1/3</div>
  <div class="h-2/3 ...">h-2/3</div>
</div>
<div class="flex flex-col h-64 ...">
  <div class="h-1/4 ...">h-1/4</div>
  <div class="h-3/4 ...">h-3/4</div>
</div>
<div class="flex flex-col h-64 ...">
  <div class="h-1/5 ...">h-1/5</div>
  <div class="h-4/5 ...">h-4/5</div>
</div>
<div class="flex flex-col h-64 ...">
  <div class="h-1/6 ...">h-1/6</div>
  <div class="h-5/6 ...">h-5/6</div>
</div>
<div class="h-full ...">h-full</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="h-xl ...">h-xl</div>
<div class="h-lg ...">h-lg</div>
<div class="h-md ...">h-md</div>
<div class="h-sm ...">h-sm</div>
<div class="h-xs ...">h-xs</div>
<div class="h-2xs ...">h-2xs</div>
<div class="h-[200px] ...">h-[200px]</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="size-[64px] ...">size-16</div>
<div class="size-20 ...">size-20</div>
<div class="size-24 ...">size-24</div>
<div class="size-32 ...">size-32</div>
<div class="size-40 ...">size-40</div>
                `
            }
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">h-&lt;number&gt;</code> utilities to set fixed height values:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold">
                <div class="h-96 bg-color-blue-500 rounded-lg text-center flex items-end py-2">
                    <div class="mb-4 -rotate-90 text-left text-nowrap">h-96</div>
                </div>
                <div class="h-64 bg-color-blue-500 rounded-lg text-center flex items-end py-2">
                    <div class="mb-4 -rotate-90 text-left text-nowrap">h-64</div>
                </div>
                <div class="h-48 bg-color-blue-500 rounded-lg text-center flex items-end py-2">
                    <div class="mb-4 -rotate-90 text-left text-nowrap">h-48</div>
                </div>
                <div class="h-40 bg-color-blue-500 rounded-lg text-center flex items-end py-2">
                    <div class="mb-4 -rotate-90 text-left text-nowrap">h-40</div>
                </div>
                <div class="h-32 bg-color-blue-500 rounded-lg text-center flex items-end py-2">
                    <div class="mb-4 -rotate-90 text-left text-nowrap">h-32</div>
                </div>
                <div class="h-24 bg-color-blue-500 rounded-lg text-center flex items-end py-2">
                    <div class="mb-4 -rotate-90 text-left text-nowrap">h-24</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">h-full, h-&lt;fraction&gt;</code>
                utilities like h-1/2 and h-2/5 to give an element a percentage-based height:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold h-96">
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-full bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-8 -rotate-90 text-left text-nowrap">h-full</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-9/10 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-8 -rotate-90 text-left text-nowrap">h-9/10</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-3/4 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-8 -rotate-90 text-left text-nowrap">h-3/4</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-1/2 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-8 -rotate-90 text-left text-nowrap">h-1/2</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-1/3 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-8 -rotate-90 text-left text-nowrap">h-1/3</div>
                    </div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Using the container scale</code>
                Use utilities like h-sm and h-xl to set an element to a fixed height based on the container scale:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold">
                <div class="h-xl bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-6 -rotate-90 text-left text-nowrap">h-xl</div>
                </div>
                <div class="h-lg bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-6 -rotate-90 text-left text-nowrap">h-lg</div>
                </div>
                <div class="h-md bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-6 -rotate-90 text-left text-nowrap">h-md</div>
                </div>
                <div class="h-sm bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-6 -rotate-90 text-left text-nowrap">h-sm</div>
                </div>
                <div class="h-xs bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-6 -rotate-90 text-left text-nowrap">h-xs</div>
                </div>
                <div class="h-2xs bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-6 -rotate-90 text-left text-nowrap">h-2xs</div>
                </div>
                <div class="h-[200px] bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-11 -rotate-90 text-left text-nowrap">h-[200px]</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Setting both width and height</code>
                Use utilities like size-px, size-4, and size-full to set both the width and height of an element at the same time:
            </p>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="size-[64px] grid place-content-center place-items-center bg-color-blue-500 rounded-lg p-2 text-center">size-[64px]</div>
                <div class="size-20 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-20</div>
                <div class="size-24 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-24</div>
                <div class="size-32 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-32</div>
                <div class="size-40 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center">size-40</div>
            </div>
            `
        },
    ]
};

export default height;

<div className="sr-only  mb-11 space-y-4 h-[200px] h-96 h-80 h-64 h-48 h-40 h-32 h-24 w-8 -rotate-90 text-left text-nowrap h-full h-9/10 h-3/4 h-1/2 h-1/3 h-xl h-lg h-md h-sm h-xs h-2xs h-3xs">

</div>